import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const AdminTimetableManagement = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [timings, setTimings] = useState({
    morning_start: '06:00',
    afternoon_end: '15:00'
  });

  // Form state for adding/editing schedules
  const [scheduleForm, setScheduleForm] = useState({
    activityType: 'class',
    subject: '',
    day: 'Monday',
    startTime: '',
    endTime: '',
    teacherId: '',
    room: ''
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Generate time slots dynamically based on timings
  const generateTimeSlots = () => {
    const slots = [];
    const startTime = timings.morning_start;
    const endTime = timings.afternoon_end;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const slotStart = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin -= 60;
        currentHour += 1;
      }
      const slotEnd = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      slots.push(`${slotStart}-${slotEnd}`);
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  const activityTypes = [
    { value: 'assembly', label: 'Assembly', color: 'bg-purple-100 border-purple-300 text-purple-800' },
    { value: 'class', label: 'Class', color: 'bg-blue-100 border-blue-300 text-blue-800' },
    { value: 'break', label: 'Break', color: 'bg-green-100 border-green-300 text-green-800' },
    { value: 'meal', label: 'Meal', color: 'bg-orange-100 border-orange-300 text-orange-800' },
    { value: 'play', label: 'Play/Recess', color: 'bg-pink-100 border-pink-300 text-pink-800' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadClassSchedules();
    }
  }, [selectedClass]);

  const loadInitialData = async () => {
    try {
      const [classesRes, subjectsRes, teachersRes, timingsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.CLASSES}`),
        fetch(`${API_ENDPOINTS.SUBJECTS}`),
        fetch(`${API_ENDPOINTS.TEACHERS}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/timings/config`)
      ]);

      if (classesRes.ok) {
        const classesData = await classesRes.json();
        setClasses(classesData);
        if (classesData.length > 0) setSelectedClass(classesData[0]);
      }

      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        setSubjects(subjectsData);
      }

      if (teachersRes.ok) {
        const teachersData = await teachersRes.json();
        setTeachers(teachersData);
      }

      if (timingsRes.ok) {
        const timingsData = await timingsRes.json();
        setTimings({
          morning_start: timingsData.morning_start || '06:00',
          afternoon_end: timingsData.afternoon_end || '15:00'
        });
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClassSchedules = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/class/${encodeURIComponent(selectedClass)}`);
      if (response.ok) {
        const schedulesData = await response.json();
        setSchedules(schedulesData);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const getAvailableSubjects = () => {
    return subjects.filter(subject => subject.className === selectedClass);
  };

  const getAvailableTeachers = (subject) => {
    return teachers.filter(teacher =>
      teacher.active &&
      teacher.assignedSubjects &&
      teacher.assignedSubjects.some(s => s.toLowerCase() === subject.toLowerCase())
    );
  };

  const getScheduleForSlot = (day, timeSlot) => {
    return schedules.find(schedule => {
      if (schedule.day !== day) return false;

      // Parse the time slot (e.g., "08:00-09:00" -> start: "08:00", end: "09:00")
      const [slotStart, slotEnd] = timeSlot.split('-');

      // Check if schedule overlaps with this time slot
      return schedule.startTime <= slotStart && schedule.endTime >= slotEnd;
    });
  };

  const handleAddSchedule = async () => {
    if (!scheduleForm.subject || !scheduleForm.startTime || !scheduleForm.endTime || !scheduleForm.teacherId) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          className: selectedClass,
          ...scheduleForm
        })
      });

      if (response.ok) {
        alert('Schedule added successfully!');
        setShowAddModal(false);
        resetForm();
        loadClassSchedules();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add schedule');
      }
    } catch (error) {
      console.error('Error adding schedule:', error);
      alert('Failed to add schedule');
    }
  };

  const handleEditSchedule = async () => {
    if (!editingSchedule) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/${editingSchedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleForm)
      });

      if (response.ok) {
        alert('Schedule updated successfully!');
        setEditingSchedule(null);
        resetForm();
        loadClassSchedules();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update schedule');
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Failed to update schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/${scheduleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Schedule deleted successfully!');
        loadClassSchedules();
      } else {
        alert('Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule');
    }
  };

  const resetForm = () => {
    setScheduleForm({
      activityType: 'class',
      subject: '',
      day: 'Monday',
      startTime: '',
      endTime: '',
      teacherId: '',
      room: ''
    });
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      activityType: schedule.activityType || 'class',
      subject: schedule.subject,
      day: schedule.day,
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      teacherId: schedule.teacherId || '',
      room: schedule.room || ''
    });
    setShowAddModal(true);
  };

  const getActivityColor = (activityType) => {
    const activityTypeObj = activityTypes.find(at => at.value === activityType);
    return activityTypeObj ? activityTypeObj.color : 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const assignedSchedules = schedules.filter(schedule => schedule.teacherId).length;
  const roomCoverage = schedules.filter(schedule => schedule.room).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading timetable management...</div>
      </div>
    );
  }

  return (
    <AdminPageShell>
        <AdminPageHeader
          eyebrow="Scheduling"
          title="Timetable Management"
          description="Create weekly schedules, assign teachers, review room coverage, and keep class periods conflict-free."
          actions={(
          <>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              disabled={!selectedClass}
            >
              Add Schedule
            </button>
          </>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Classes" value={classes.length} tone="sky" detail="Available schedules" />
          <StatCard label="Periods" value={schedules.length} tone="emerald" detail={selectedClass || 'Selected class'} />
          <StatCard label="Teacher Assigned" value={assignedSchedules} tone={assignedSchedules < schedules.length ? 'amber' : 'violet'} detail="Periods with teacher" />
          <StatCard label="Room Coverage" value={roomCoverage} tone="slate" detail="Periods with room" />
        </div>

        {schedules.some(schedule => !schedule.teacherId || !schedule.room) && (
          <NoticePanel title="Schedule completion" tone="amber">
            Some periods are missing a teacher or room. Complete those details before publishing the timetable.
          </NoticePanel>
        )}

        {/* Class Selection */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <label className="text-lg font-semibold text-gray-800">Select Class:</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="border rounded px-4 py-2 text-lg"
            >
              {classes.map((cls, index) => (
                <option key={index} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Weekly Timetable Grid */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {selectedClass} - Weekly Timetable
          </h2>

          <div className="overflow-x-auto">
            <div className="grid grid-cols-6 gap-2 min-w-max">
              {/* Time Slots Header */}
              <div className="font-semibold text-gray-700 p-3 border-b-2">Time</div>
              {daysOfWeek.map(day => (
                <div key={day} className="font-semibold text-gray-700 p-3 border-b-2 text-center">
                  {day}
                </div>
              ))}

              {/* Time Slots */}
              {timeSlots.map(timeSlot => (
                <React.Fragment key={timeSlot}>
                  <div className="font-medium text-gray-600 p-3 border-r bg-gray-50">
                    {timeSlot}
                  </div>
                  {daysOfWeek.map(day => {
                    const schedule = getScheduleForSlot(day, timeSlot);
                    return (
                      <div
                        key={`${day}-${timeSlot}`}
                        className="border rounded-lg p-3 min-h-[80px] cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => schedule ? openEditModal(schedule) : null}
                      >
                        {schedule ? (
                          <div className={`rounded-lg p-2 h-full ${getActivityColor(schedule.activityType)}`}>
                            <div className="font-semibold text-sm">{schedule.subject}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              {schedule.teacherName || 'No teacher assigned'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {schedule.room || 'No room'}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSchedule(schedule.id);
                              }}
                              className="mt-2 text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className="text-gray-400 text-sm flex items-center justify-center h-full">
                            Empty
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Add/Edit Schedule Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
                </h3>

                <div className="space-y-4">
                  {/* Activity Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Type *
                    </label>
                    <select
                      value={scheduleForm.activityType}
                      onChange={(e) => setScheduleForm({...scheduleForm, activityType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {activityTypes.map(activity => (
                        <option key={activity.value} value={activity.value}>
                          {activity.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subject Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject/Activity *
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.subject}
                      onChange={(e) => setScheduleForm({...scheduleForm, subject: e.target.value, teacherId: ''})}
                      placeholder="e.g., ENGLISH, Morning Assembly, Lunch Break"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Day Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day *
                    </label>
                    <select
                      value={scheduleForm.day}
                      onChange={(e) => setScheduleForm({...scheduleForm, day: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {daysOfWeek.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  {/* Start Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={scheduleForm.startTime}
                      onChange={(e) => setScheduleForm({...scheduleForm, startTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* End Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={scheduleForm.endTime}
                      onChange={(e) => setScheduleForm({...scheduleForm, endTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Teacher Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teacher *
                    </label>
                    <select
                      value={scheduleForm.teacherId}
                      onChange={(e) => setScheduleForm({...scheduleForm, teacherId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a teacher</option>
                      {teachers.filter(teacher => teacher.active).map(teacher => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.firstName} {teacher.lastName} - {teacher.assignedSubjects?.join(', ') || 'No subjects'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Room */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Room
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.room}
                      onChange={(e) => setScheduleForm({...scheduleForm, room: e.target.value})}
                      placeholder="e.g., Room 101"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={editingSchedule ? handleEditSchedule : handleAddSchedule}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    {editingSchedule ? 'Update Schedule' : 'Add Schedule'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingSchedule(null);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </AdminPageShell>
  );
};

export default AdminTimetableManagement;
